import { defineModal, confirmModalPayloadSchema, type ModalRegistryMap } from '@noor/lib';
import {
  CancelFoodOrderModal,
  CreateTestFoodOrderModal,
  cancelFoodOrderPayloadSchema,
  createTestFoodOrderPayloadSchema,
} from '@/features/food-order-actions';
import {
  CancelOrderModal,
  cancelOrderPayloadSchema,
  ReOfferModal,
  reOfferPayloadSchema,
} from '@/features/order-actions';
import {
  CreateNoteModal,
  createNotePayloadSchema,
  OrderNotesModal,
  orderNotesPayloadSchema,
} from '@/features/create-order-note';
import {
  ChatMacroModal,
  chatMacroPayloadSchema,
  CloseChatModal,
  closeChatPayloadSchema,
  SwitchOperatorModal,
  switchOperatorPayloadSchema,
} from '@/features/conversation-actions';
import {
  StatusPriorityModal,
  statusPriorityPayloadSchema,
} from '@/features/status-priority';
import { OrderOffersModal, orderOffersPayloadSchema } from '@/widgets/order-map';
import { ConfirmModal } from './confirm-modal';

/**
 * Concrete modal registry: maps every registered modal id to its definition
 * (`{ component, schema }`). Lives in `app` because it imports feature modals —
 * `shared` only defines the contract. Mirrors the `app/mock` decision.
 *
 * `defineModal(id, …)` pins each entry to its id, so the component (typed by the
 * payload it consumes) and the schema (typed by the payload it produces) are
 * both checked against THAT id. `ModalRegistryMap` is exhaustive: a missing id
 * — or a component/schema that doesn't match its payload — is a compile error.
 */
export const modalRegistry: ModalRegistryMap = {
  'cancel-food-order': defineModal('cancel-food-order', {
    component: CancelFoodOrderModal,
    schema: cancelFoodOrderPayloadSchema,
  }),
  'create-test-food-order': defineModal('create-test-food-order', {
    component: CreateTestFoodOrderModal,
    schema: createTestFoodOrderPayloadSchema,
  }),
  confirm: defineModal('confirm', {
    component: ConfirmModal,
    schema: confirmModalPayloadSchema,
  }),
  'cancel-order': defineModal('cancel-order', {
    component: CancelOrderModal,
    schema: cancelOrderPayloadSchema,
  }),
  're-offer': defineModal('re-offer', {
    component: ReOfferModal,
    schema: reOfferPayloadSchema,
  }),
  'create-note': defineModal('create-note', {
    component: CreateNoteModal,
    schema: createNotePayloadSchema,
  }),
  'order-notes': defineModal('order-notes', {
    component: OrderNotesModal,
    schema: orderNotesPayloadSchema,
  }),
  'close-chat': defineModal('close-chat', {
    component: CloseChatModal,
    schema: closeChatPayloadSchema,
  }),
  'switch-operator': defineModal('switch-operator', {
    component: SwitchOperatorModal,
    schema: switchOperatorPayloadSchema,
  }),
  'chat-macro': defineModal('chat-macro', {
    component: ChatMacroModal,
    schema: chatMacroPayloadSchema,
  }),
  'order-offers': defineModal('order-offers', {
    component: OrderOffersModal,
    schema: orderOffersPayloadSchema,
  }),
  'status-priority': defineModal('status-priority', {
    component: StatusPriorityModal,
    schema: statusPriorityPayloadSchema,
  }),
};
